import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  Select,
  Checkbox,
  Textarea,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Alert,
  AlertIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import { saveAs } from 'file-saver'
import Axios from '../../../Axios'
import { CollectionTable } from './History'
import Reconstruction from './Reconstruction'
import {
  Button,
  TableBox,
  Pager,
  Loading,
  PeriodField,
  currentMonth,
  isMonth,
  money,
  get,
  writers,
  labels,
  useBillingAction
} from './shared'

export default function CollectionUpload() {
  const { user } = useSelector((s) => s.auth)
  const canWrite = writers.includes(user?.role)
  const { busy, action } = useBillingAction()
  const [month, setMonth] = useState(currentMonth)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [file, setFile] = useState(null)
  const [fileKey, setFileKey] = useState(0)
  const [confirmAmount, setConfirmAmount] = useState(false)
  const [preview, setPreview] = useState(null)
  const [reason, setReason] = useState('')
  const [showCharts, setShowCharts] = useState(false)
  const validMonth = isMonth(month)
  const periods = useQuery(
    ['billing', 'periods', month],
    () => get('periods', { period: month }),
    { enabled: validMonth, retry: false }
  )
  const reconciliation = useQuery(
    ['billing', 'reconciliation', month, page, search, status],
    () => get('reconciliation', { period: month, page, search, status }),
    { enabled: validMonth, retry: false }
  )
  const charts = useQuery(
    ['billing', 'charts', month],
    () => get('charts', { to: month, months: 12 }),
    { enabled: showCharts && validMonth, retry: false, staleTime: 60000 }
  )
  const summary = reconciliation.data?.summary
  const closed = Boolean(reconciliation.data?.state?.closedAt)
  const metrics = charts.data || []
  const changeMonth = (value) => {
    setMonth(value)
    setPage(0)
    setPreview(null)
    setFile(null)
    setFileKey((key) => key + 1)
    setConfirmAmount(false)
    setReason('')
  }
  const upload = () =>
    action(async () => {
      const body = new FormData()
      body.append('file', file)
      body.append('period', month)
      body.append('confirmReportedAmount', String(confirmAmount))
      const response = await Axios.post('/billing/imports/preview', body)
      setPreview(response.data)
      setReason('')
    })
  return (
    <Box>
      <Flex justify="space-between" align="end" gap={4} wrap="wrap" mb={5}>
        <Text color="gray.600" maxW="600px">
          Carga el resultado enviado por Carabineros y revisa cuánto se cobró a
          cada socio.
        </Text>
        <PeriodField value={month} onChange={changeMonth} disabled={busy} />
      </Flex>
      {closed && (
        <Alert status="info" mb={4}>
          <AlertIcon />
          Este mes está cerrado. Un administrador puede reabrirlo para corregir
          una carga.
        </Alert>
      )}
      {canWrite && (
        <Box bg="beige.50" borderRadius={8} p={{ base: 4, md: 6 }} mb={6}>
          <Heading size="sm" color="green.500">
            Cargar archivo de cobro
          </Heading>
          <Text my={3} fontSize="sm">
            Selecciona el período correcto y carga un Excel o TXT. Primero
            revisa el resultado; los registros se actualizan al aplicar la
            respuesta.
          </Text>
          <Flex gap={3} wrap="wrap">
            <Input
              aria-label="Archivo de respuesta"
              key={fileKey}
              isDisabled={busy || closed}
              type="file"
              accept=".xlsx,.txt"
              p={1}
              maxW="450px"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null)
                setPreview(null)
                setConfirmAmount(false)
              }}
            />
            <Button
              isLoading={busy}
              isDisabled={!file || !validMonth || closed}
              onClick={upload}
            >
              Revisar archivo
            </Button>
          </Flex>
          {file?.name.toLowerCase().endsWith('.txt') && (
            <Checkbox
              mt={3}
              isChecked={confirmAmount}
              onChange={(e) => setConfirmAmount(e.target.checked)}
            >
              Finanzas confirmó que «monto informado» corresponde al cobro real
              en este TXT de aplicados.
            </Checkbox>
          )}
          {preview && (
            <Box mt={4}>
              <Alert status={preview.errors?.length ? 'error' : 'info'}>
                <AlertIcon />
                <Text>
                  {preview.filename}: {preview.count} registros,{' '}
                  {money(preview.total)} cobrados, {preview.rejected}{' '}
                  rechazados, {preview.duplicates} duplicados idénticos
                  omitidos. {preview.conflicts} conflictos y {preview.unmatched}{' '}
                  sin socio asociado.
                </Text>
              </Alert>
              {preview.unmatched > 0 && (
                <Text mt={2} fontSize="sm">
                  Los resultados sin socio se conservarán para revisión.
                  Registra su ficha con el RUT correspondiente para asociarlos.
                </Text>
              )}
              {preview.errors?.map((e, i) => (
                <Text color="red.700" fontSize="sm" key={i}>
                  Fila {e.line}: {e.message}
                </Text>
              ))}
              {preview.conflicts > 0 && (
                <>
                  <Text mt={3}>
                    Las siguientes filas cambiarían resultados anteriores:
                  </Text>
                  {preview.conflictRows?.slice(0, 10).map((r) => (
                    <Text fontSize="sm" key={r._id}>
                      {r.rut}: {money(r.previousAmount)} → {money(r.amount)}
                    </Text>
                  ))}
                  <Textarea
                    aria-label="Motivo de corrección"
                    mt={3}
                    placeholder="Motivo de la corrección (mínimo 10 caracteres)"
                    maxLength={500}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </>
              )}
              {preview.state === 'APPLIED' ? (
                <Text mt={3} color="green.700">
                  Esta respuesta ya fue aplicada. No se duplicaron registros.
                </Text>
              ) : (
                <Button
                  mt={3}
                  colorScheme="green"
                  isLoading={busy}
                  isDisabled={
                    closed ||
                    !validMonth ||
                    preview.period !== month ||
                    preview.errors?.length > 0 ||
                    (preview.conflicts > 0 && reason.trim().length < 10)
                  }
                  onClick={() =>
                    action(async () => {
                      await Axios.post(
                        `/billing/imports/${preview._id}/apply`,
                        { correctionReason: reason }
                      )
                      setPreview(null)
                      setFile(null)
                      setFileKey((key) => key + 1)
                    }, 'Respuesta aplicada')
                  }
                >
                  Aplicar respuesta revisada
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}
      <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={3} mb={5}>
        {[
          ['Esperado conocido', money(summary?.expected)],
          ['Cobrado', money(summary?.actual)],
          ['Faltante registrado', money(summary?.notCollected)],
          ['Cobrado de más', money(summary?.overCollected)]
        ].map(([label, value]) => (
          <Stat key={label} bg="beige.500" borderRadius={8} p={4}>
            <StatLabel color="green.500">{label}</StatLabel>
            <StatNumber color="green.500" fontSize="xl">
              {value}
            </StatNumber>
          </Stat>
        ))}
      </SimpleGrid>
      {summary && (
        <Alert
          status={
            summary.unresolvedCount ||
            summary.estimatedCount ||
            summary.referenceCount
              ? 'warning'
              : 'info'
          }
          mb={4}
        >
          <AlertIcon />
          <Text fontSize="sm">
            {summary.expectedKind === 'RECONSTRUCTED'
              ? 'Esperado reconstruido'
              : 'Esperado de nómina'}
            : {summary.confirmedCount || 0} confirmados,{' '}
            {summary.estimatedCount || 0} estimados,{' '}
            {summary.referenceCount || 0} con referencia individual y{' '}
            {summary.unresolvedCount || 0} sin esperado. Diferencia comparable
            (real − esperado): {money(summary.difference)}.{' '}
            {summary.pendingCount || 0} pendientes de respuesta (
            {money(summary.pendingAmount)}). El total real incluye los casos sin
            esperado.
          </Text>
        </Alert>
      )}
      <Reconstruction
        key={month}
        month={month}
        state={reconciliation.data?.state}
        canWrite={canWrite}
        hasResponses={Boolean(
          periods.data?.imports?.some((i) => i.state === 'APPLIED')
        )}
      />
      <Flex gap={3} wrap="wrap">
        <Input
          maxW="350px"
          aria-label="Buscar resultado de cobro"
          placeholder="Nombre, RUT o código"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(0)
          }}
        />
        <Select
          maxW="280px"
          aria-label="Filtrar por resultado"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(0)
          }}
        >
          <option value="">Todos los resultados</option>
          {Object.entries(labels).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>
      </Flex>
      <Loading query={reconciliation} />
      <CollectionTable rows={reconciliation.data?.rows} />
      <Pager
        page={page}
        setPage={setPage}
        total={reconciliation.data?.totalRecords}
      />
      <Flex my={5} align="center" gap={3}>
        <Badge
          colorScheme={reconciliation.data?.state?.closedAt ? 'green' : 'gray'}
        >
          {reconciliation.data?.state?.closedAt
            ? 'Período cerrado'
            : 'Período abierto'}
        </Badge>
        {canWrite && !reconciliation.data?.state?.closedAt && (
          <Button
            size="sm"
            isDisabled={
              busy ||
              !validMonth ||
              !summary ||
              summary.pendingCount > 0 ||
              !periods.data?.imports?.some((item) => item.state === 'APPLIED')
            }
            onClick={() => {
              if (
                window.confirm(
                  '¿Confirmas que recibiste y revisaste la respuesta completa del período?'
                )
              )
                action(
                  () =>
                    Axios.post(`/billing/periods/${month}/close`, {
                      complete: true
                    }),
                  'Período cerrado'
                )
            }}
          >
            Cerrar período
          </Button>
        )}
        {user?.role === 'ADMINISTRADOR' &&
          reconciliation.data?.state?.closedAt && (
            <Button
              size="sm"
              isDisabled={busy}
              onClick={() =>
                action(
                  () => Axios.post(`/billing/periods/${month}/reopen`),
                  'Período reabierto'
                )
              }
            >
              Reabrir para corregir
            </Button>
          )}
      </Flex>
      <Heading size="sm" color="green.500" mt={7}>
        Archivos del período
      </Heading>
      <Loading query={periods} />
      <TableBox>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Archivo</Th>
              <Th>Estado</Th>
              <Th>Registros</Th>
              <Th>Importado</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {periods.data?.imports
              ?.filter((i) => i.period === month)
              .map((i) => (
                <Tr key={i._id}>
                  <Td>{i.filename}</Td>
                  <Td>
                    {
                      {
                        APPLIED: 'Aplicado',
                        PREVIEW: 'Por revisar',
                        PREPARING: 'Preparando',
                        FAILED: 'Error'
                      }[i.state]
                    }
                  </Td>
                  <Td>{i.count}</Td>
                  <Td>{new Date(i.createdAt).toLocaleString('es-CL')}</Td>
                  <Td>
                    <Flex gap={2}>
                      {canWrite && i.state === 'PREVIEW' && (
                        <Button
                          size="xs"
                          onClick={() =>
                            action(async () => {
                              setPreview(await get(`imports/${i._id}`))
                              setReason('')
                            })
                          }
                        >
                          Revisar
                        </Button>
                      )}
                      <Button
                        size="xs"
                        onClick={() =>
                          action(async () => {
                            const response = await Axios.get(
                              `/billing/imports/${i._id}/file`,
                              { responseType: 'blob' }
                            )
                            saveAs(response.data, i.filename)
                          })
                        }
                      >
                        Original
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      </TableBox>

      <Accordion
        allowToggle
        mt={8}
        onChange={(index) => setShowCharts(index === 0)}
      >
        <AccordionItem borderColor="beige.600">
          <AccordionButton py={4} color="green.500" fontWeight="bold">
            <Box flex="1" textAlign="left">
              Evolución de los cobros
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={0} pt={5}>
            {showCharts && (
              <>
                <Text mb={5} color="gray.600">
                  Últimos 12 meses. El esperado conocido incluye estimaciones
                  identificadas. Compara con el real de socios con esperado; el
                  real total también incluye los casos sin clasificar. Un
                  pendiente de respuesta no equivale a rechazo.
                </Text>
                <Loading query={charts} />
                <Box h="340px" borderWidth="1px" borderRadius="lg" p={3}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis
                        tickFormatter={(v) => `${Math.round(v / 1000)} mil`}
                      />
                      <Tooltip formatter={(v) => money(v)} />
                      <Legend />
                      <Bar
                        dataKey="expected"
                        name="Esperado conocido"
                        fill="#3b6d3b"
                      />
                      <Bar
                        dataKey="comparableActual"
                        name="Real con esperado"
                        fill="#83b678"
                      />
                      <Bar dataKey="actual" name="Real total" fill="#88a9c4" />
                      <Bar
                        dataKey="notCollected"
                        name="Faltante registrado"
                        fill="#d77957"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box h="300px" mt={6}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="actualCount"
                        name="Socios con cobro"
                        fill="#83b678"
                      />
                      <Bar
                        dataKey="rejectedCount"
                        name="Rechazados"
                        fill="#d77957"
                      />
                      <Bar
                        dataKey="unresolvedCount"
                        name="Sin esperado"
                        fill="#b18dba"
                      />
                      <Bar
                        dataKey="estimatedCount"
                        name="Con grupo estimado"
                        fill="#d2a155"
                      />
                      <Bar
                        dataKey="pendingCount"
                        name="Pendientes"
                        fill="#a0aec0"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Heading size="sm" color="green.500" mt={5}>
                  Motivos de rechazo · {month}
                </Heading>
                <TableBox>
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Motivo</Th>
                        <Th isNumeric>Socios</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.entries(
                        metrics.find((m) => m.period === month)?.reasons || {}
                      ).map(([name, count]) => (
                        <Tr key={name}>
                          <Td>{name}</Td>
                          <Td isNumeric>{count}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableBox>
              </>
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  )
}
